import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { api, apiErr } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";
import { ShieldCheck } from "lucide-react";
import { PasswordField, passwordValid, PASSWORD_RULE_TEXT } from "../components/PasswordField";

export default function SetPassword() {
  const [params] = useSearchParams();
  const token = params.get("token");
  const navigate = useNavigate();
  const { setUser } = useAuth();
  const [info, setInfo] = useState(null);
  const [status, setStatus] = useState("checking"); // checking | ok | invalid
  const [pw, setPw] = useState("");
  const [pw2, setPw2] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!token) { setStatus("invalid"); return; }
    api.get(`/auth/setup-token/${token}`)
      .then((r) => { setInfo(r.data); setStatus("ok"); })
      .catch((e) => { setError(apiErr(e)); setStatus("invalid"); });
  }, [token]);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (!passwordValid(pw)) return setError(PASSWORD_RULE_TEXT);
    if (pw !== pw2) return setError("Passwords do not match.");
    setBusy(true);
    try {
      const { data } = await api.post("/auth/set-password", { token, password: pw });
      setUser(data);
      toast.success("Password set — welcome!");
      navigate(data.role === "employee" ? "/me" : data.role === "client" ? "/client" : "/dashboard", { replace: true });
    } catch (e2) {
      setError(apiErr(e2));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-[#0a0a0a] p-6">
      <div className="w-full max-w-sm bg-white border border-black p-8">
        <div className="flex items-center gap-2 mb-6">
          <div className="h-8 w-8 bg-black text-white flex items-center justify-center font-black">P</div>
          <span className="font-black tracking-tight">POSTINGS INC</span>
        </div>

        {status === "checking" && <p className="text-sm text-muted-foreground font-mono">Verifying your invitation…</p>}

        {status === "invalid" && (
          <div data-testid="setpw-invalid">
            <h1 className="text-xl font-black tracking-tight">Link unavailable</h1>
            <p className="text-sm text-muted-foreground mt-2">{error || "This invitation link is invalid or has expired."}</p>
            <button onClick={() => navigate("/login")} className="mt-6 w-full bg-black text-white py-3 text-sm font-medium hover:bg-neutral-800 transition-colors">Go to sign in</button>
          </div>
        )}

        {status === "ok" && (
          <>
            <h1 className="text-xl font-black tracking-tight">Set your password</h1>
            <p className="text-sm text-muted-foreground mt-1 flex items-center gap-1.5">
              <ShieldCheck className="h-3.5 w-3.5" /> {info?.email}
            </p>
            {info?.company_name && (
              <p className="text-xs font-mono uppercase tracking-widest text-muted-foreground mt-1">{info.company_name}</p>
            )}
            <form onSubmit={submit} className="mt-6 space-y-4">
              <div>
                <label className="text-xs font-mono uppercase tracking-widest text-muted-foreground">New password</label>
                <PasswordField testId="setpw-password" meter value={pw} onChange={(e) => setPw(e.target.value)} />
              </div>
              <div>
                <label className="text-xs font-mono uppercase tracking-widest text-muted-foreground">Confirm password</label>
                <PasswordField testId="setpw-confirm" value={pw2} onChange={(e) => setPw2(e.target.value)} autoComplete="new-password" />
              </div>
              {error && <div data-testid="setpw-error" className="text-sm text-destructive border border-destructive/30 bg-destructive/5 px-3 py-2">{error}</div>}
              <button data-testid="setpw-submit" disabled={busy} className="w-full bg-black text-white py-3 text-sm font-medium hover:bg-neutral-800 transition-colors disabled:opacity-50">
                {busy ? "Saving…" : "Set password & sign in"}
              </button>
            </form>
          </>
        )}
      </div>
    </div>
  );
}
